import {
  Activity,
  Aperture,
  ArrowLeft,
  ArrowRight,
  ArrowUpToLine,
  Award,
  Box,
  BookOpen,
  Bot,
  Briefcase,
  Calendar,
  ChartBar,
  ChartColumn,
  Clipboard,
  Compass,
  CreditCard,
  Database,
  Disc,
  Droplet,
  FileSpreadsheet,
  FileText,
  Funnel,
  FlaskConical,
  GitBranch,
  GitMerge,
  Globe,
  GraduationCap,
  Grid3x3,
  Heart,
  House,
  Image,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Lightbulb,
  Lock,
  MapPin,
  Megaphone,
  Menu,
  Monitor,
  Newspaper,
  Package,
  Percent,
  Scale,
  Server,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Star,
  Tag,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  UserCheck,
  UserPlus,
  Users,
  Zap,
  Share2,
} from 'lucide-angular';

import {
  ArrowDownAZ,
  ArrowDownZA,
  // Acciones de filas en tablas (listado de menús)
  ArrowLeftRight,
  EllipsisVertical,
  TextCursorInput,
  Building2,
  ChevronDown,
  Check,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleX,
  ClipboardList,
  Flag,
  CornerDownRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FilePlus,
  Folder,
  FolderPlus,
  FolderUp,
  History,
  // Nueva versión y clonar (listados de fórmulas)
  CirclePlus,
  Copy,
  Info,
  Link,
  Unlink,
  LogOut,
  Maximize,
  Minimize,
  Minus,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  SkipForward,
  Sparkles,
  SquarePen,
  Trash2,
  User,
  X,
} from 'lucide-angular';

// Iconos que lucide-angular 1.0.0 todavía no trae (ver custom-icons.ts)
import { BroomSparkles } from './custom-icons';

/**
 * Catálogo de iconos de lucide. `pick()` solo empaqueta los declarados y cada
 * uno pesa ~1,4 KB, así que el catálogo es curado y vive en un solo lugar.
 *
 * Para sumar uno: importarlo arriba y agregarlo al bloque que corresponda. Los
 * de dominio se ofrecen en los selectores de iconos; los de interfaz, no.
 */

/** Iconos de dominio: los que el usuario puede elegir para una entidad */
export const APP_DOMAIN_ICONS = {
  Award,
  Landmark,
  Scale,
  Briefcase,
  TrendingUp,
  ChartBar,
  ChartColumn,
  Percent,
  CreditCard,
  Users,
  UserCheck,
  UserPlus,
  GraduationCap,
  Heart,
  Tag,
  Star,
  BookOpen,
  FileText,
  Clipboard,
  FlaskConical,
  Lightbulb,
  Aperture,
  Target,
  Compass,
  Globe,
  MapPin,
  House,
  Truck,
  Package,
  Box,
  ShoppingCart,
  ShoppingBag,
  Bot,
  Monitor,
  Server,
  Database,
  Lock,
  Shield,
  Zap,
  Droplet,
  Image,
  Megaphone,
  Newspaper,
  Settings,
  Activity,
  Calendar,
  Disc,
  Grid3x3,
  LayoutGrid,
  LayoutDashboard,
  Menu,
  GitBranch,
  GitMerge,
};

/** Iconos de interfaz: chevrons, cerrar, zoom, editar, borrar… */
export const APP_UI_ICONS = {
  ArrowDownAZ,
  ArrowDownZA,
  // Acciones de filas en tablas (listado de menús)
  ArrowLeftRight,
  EllipsisVertical,
  TextCursorInput,
  ArrowLeft,
  ArrowRight,
  ArrowUpToLine,
  // Switch de versión del layout (beliv-header y home-header, solo admins)
  LayoutTemplate,
  // Selector de compañía del header de master (beliv-header)
  Building2,
  ChevronDown,
  Check,
  ChevronUp,
  // Variación negativa de las tarjetas KPI (dashboards de rayfin)
  TrendingDown,
  // Pasos del PDCA (cabeceras del acordeón y su estado de error)
  CircleAlert,
  CircleCheck,
  CircleX,
  ClipboardList,
  Flag,
  CornerDownRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FilePlus,
  FileSpreadsheet,
  Folder,
  FolderPlus,
  FolderUp,
  History,
  // Nueva versión y clonar (listados de fórmulas)
  CirclePlus,
  Copy,
  Funnel,
  Info,
  Link,
  Unlink,
  LogOut,
  Maximize,
  Minimize,
  Minus,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  // Compartir archivos y carpetas (share-file-modal)
  Share2,
  SkipForward,
  // Limpiar (no viene en lucide-angular 1.0.0, ver custom-icons.ts)
  BroomSparkles,
  // Acceso a la comunidad de IA en el banner del inicio (home-header)
  Sparkles,
  SquarePen,
  Trash2,
  User,
  X,
};

/** Todo lo que registra `IconsModule` */
export const APP_ICONS = {
  ...APP_DOMAIN_ICONS,
  ...APP_UI_ICONS,
};

/** `FlaskConical` → `flask-conical`, que es como los nombra la plantilla */
export function toKebabCase(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

/** Opciones de los selectores, derivadas del catálogo: uno sin registrar no se dibujaría. */
export const ICON_OPTIONS: string[] = Object.keys(APP_DOMAIN_ICONS).map(toKebabCase);

/**
 * Todo lo empaquetado, en kebab: dice si un icono se dibuja directo o hay que
 * buscarlo en el set completo (ver LucideIconLoaderService).
 */
export const BUNDLED_ICON_NAMES: ReadonlySet<string> =
  new Set(Object.keys(APP_ICONS).map(toKebabCase));
